import { FC, useMemo } from 'react'
import { alpha } from '@mui/material/styles'
import { Box, ButtonBase, Typography, useTheme } from '@mui/material'
import { matchPath, useLocation, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import R4RIcon, { R4RIconName } from 'components/R4RIcon'
import { useUsersProfile } from 'services/profile/profile.hooks'

interface SidebarProps {
  isOnlyOwner: boolean
}

const Sidebar: FC<SidebarProps> = ({ isOnlyOwner }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { formatMessage } = useIntl()
  const { data: usersProfile, userInitials } = useUsersProfile()

  const navItems: {
    value: string
    icon: R4RIconName
    label: string
    hiddenForOwner?: boolean
  }[] = [
    {
      value: '/',
      icon: 'home',
      label: formatMessage({ id: 'discounts.panel', defaultMessage: 'Panel' }),
      hiddenForOwner: true
    },
    {
      value: '/faults',
      icon: 'constructTab',
      label: formatMessage({ id: 'discounts.helpDesk', defaultMessage: 'HelpDesk' })
    },
    {
      value: '/board',
      icon: 'megaphoneTab',
      label: formatMessage({ id: 'dashboard.announcements', defaultMessage: 'Komunikaty' }),
      hiddenForOwner: true
    },
    {
      value: '/additional-services',
      icon: 'documentTab',
      label: formatMessage({ id: 'dashboard.services', defaultMessage: 'Umowy' }),
      hiddenForOwner: true
    },
    {
      value: '/discounts',
      icon: 'pricetag',
      label: formatMessage({ id: 'dashboard.discounts', defaultMessage: 'Rabaty' }),
      hiddenForOwner: true
    },
    {
      value: '/user',
      icon: 'person',
      label: formatMessage({ id: 'dashboard.profile', defaultMessage: 'Profil' })
    }
  ]

  const currentValue = useMemo(() => {
    const patterns = [
      '/additional-services/*',
      '/faults/*',
      '/discounts/*',
      '/board/*',
      '/user/*',
      '/',
      '/offer/*'
    ]

    for (const pattern of patterns) {
      const possibleMatch = matchPath(pattern, pathname)
      if (possibleMatch !== null) {
        return possibleMatch.pathnameBase
      }
    }

    return null
  }, [pathname])

  return (
    <Box
      component='aside'
      sx={{
        position: 'fixed',
        top: '56px',
        bottom: 0,
        left: 0,
        width: '240px',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        borderRight: `1px solid ${theme.r4r.greyLight}`,
        paddingTop: '20px',
        zIndex: 101
      }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {navItems.map(({ value, icon, label, hiddenForOwner }) => {
          const isActive = currentValue === value
          if (isOnlyOwner && hiddenForOwner) {
            return null
          }
          return (
            <ButtonBase
              key={value}
              onClick={() => navigate(value)}
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                padding: '12px 20px',
                borderLeft: `3px solid ${isActive ? theme.r4r.navy : 'transparent'}`,
                backgroundColor: isActive ? alpha(theme.r4r.navy, 0.08) : 'transparent',
                color: isActive ? theme.r4r.navy : theme.r4r.grey,
                '&:hover': {
                  backgroundColor: isActive ? alpha(theme.r4r.navy, 0.08) : theme.r4r.greyBg
                }
              }}>
              <Box
                sx={{
                  width: '20px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                <R4RIcon name={icon} size={18} />
              </Box>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: isActive ? 700 : 500,
                  lineHeight: 1
                }}>
                {label}
              </Typography>
            </ButtonBase>
          )
        })}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          borderTop: `1px solid ${theme.r4r.greyLight}`,
          padding: '17px 20px 16px'
        }}>
        <Box
          sx={{
            width: '36px',
            height: '36px',
            borderRadius: '18px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: theme.r4r.gradient,
            color: '#fff',
            fontSize: '12px',
            fontWeight: 700
          }}>
          {userInitials}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            noWrap
            sx={{ fontSize: '13px', fontWeight: 600, color: theme.r4r.ink }}>
            {[usersProfile?.firstName, usersProfile?.lastName].filter(Boolean).join(' ')}
          </Typography>
          <Typography noWrap sx={{ fontSize: '11px', color: theme.r4r.grey }}>
            {usersProfile?.email}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export default Sidebar
